import React from "react";
import DropdownMenu from "../../DropdownMenu";

/**
 * Creates props for completed volumes graph to be used with BlankCard component
 * @param {Array} data - Array of daily data objects containing completed volumes information
 * @param {Object} options - Additional options like height, chartHeight, goal, etc.
 * @returns {Object} - Props object ready to spread into BlankCard component
 */
export const createCompletedVolumes = (data, options = {}, viewState, setViewState) => {
  // Set default values for options
  const {
    height = 300,
    chartHeight = 250,
    goal = 70, // Default goal percentage if not provided
  } = options;

  const viewOptions = [
    { id: "day", name: "Day" },
    { id: "month", name: "Month" },
    { id: "year", name: "Year" }
  ];

  // Filter out invalid data
  const validData = data.filter(item => 
    item && typeof item.completedVolumes !== 'undefined' && item.completedVolumes !== null
  );
  
  // Calculate total completed volumes
  const totalVolumes = validData.length > 0
    ? validData.reduce((sum, item) => sum + (item.completedVolumes || 0), 0)
    : 0;
  
  // Calculate average volumes per day
  const avgVolumesPerDay = validData.length > 0
    ? (totalVolumes / validData.length).toFixed(1)
    : 0;
  
  // Calculate linked appointments percentage (if available in data)
  let linkedApptsPercentage = 0;
  const dataWithLinkedInfo = validData.filter(item => 
    item.linkedAppointments !== undefined && item.completedVolumes > 0
  );
  
  if (dataWithLinkedInfo.length > 0) {
    const totalLinked = dataWithLinkedInfo.reduce((sum, item) => sum + (item.linkedAppointments || 0), 0);
    const totalApptsInLinkedData = dataWithLinkedInfo.reduce((sum, item) => sum + (item.completedVolumes || 0), 0);
    linkedApptsPercentage = ((totalLinked / totalApptsInLinkedData) * 100).toFixed(1);
  }
  
  // Calculate patients per chair (if available in data)
  let patientsPerChair = 0;
  const dataWithChairInfo = validData.filter(item => 
    item.averageAppointmentsPerChair !== undefined && item.averageAppointmentsPerChair > 0
  );

  if (dataWithChairInfo.length > 0) {
    const totalAverageAppointments = dataWithChairInfo.reduce((sum, item) => sum + item.averageAppointmentsPerChair, 0);
    patientsPerChair = (totalAverageAppointments / dataWithChairInfo.length).toFixed(1);
  }

  let seriesData = [];
  let categories = [];

  // Process data for monthly view
  const month_to_values = {};
  // Process data for yearly view
  const year_to_values = {};

  // Prepare aggregated data for both monthly and yearly views
  validData.forEach(item => {
    if (!item.date) return;
    
    // For monthly aggregation
    const dateParts = item.date.split('-');
    if (dateParts.length >= 2) {
      const monthYear = `${dateParts[0]}-${dateParts[1]}`; // YYYY-MM
      
      if (!month_to_values[monthYear]) {
        month_to_values[monthYear] = [];
      }
      
      month_to_values[monthYear].push(item.completedVolumes || 0);
    }
    
    // For yearly aggregation
    const year = dateParts[0]; // YYYY
    if (year) {
      if (!year_to_values[year]) {
        year_to_values[year] = [];
      }
      
      year_to_values[year].push(item.completedVolumes || 0);
    }
  });

  // Function to format month labels
  const formatMonthLabel = (monthYear) => {
    if (!monthYear || monthYear.split('-').length < 2) return monthYear;
    
    const [year, month] = monthYear.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthIndex = parseInt(month, 10) - 1;
    
    return `${monthNames[monthIndex]} ${year}`;
  };


  // Prepare data for the line chart
  if (viewState === "day") {
    seriesData = validData.map(item => item.completedVolumes || 0);
    categories = validData.map(item => item.date);
  } else if (viewState === "month") {
    // Monthly view - use aggregated data
    const sortedMonths = Object.keys(month_to_values).sort();
    
    seriesData = sortedMonths.map(month => {
      const values = month_to_values[month];
      return values.length > 0 ? 
        values.reduce((sum, val) => sum + val, 0) : 0;  // Sum instead of average
    });
    
    categories = sortedMonths.map(month => formatMonthLabel(month));
  } else if (viewState === "year") {
    // Yearly view - use aggregated data
    const sortedYears = Object.keys(year_to_values).sort();
    
    seriesData = sortedYears.map(year => {
      const values = year_to_values[year];
      return values.length > 0 ? 
        values.reduce((sum, val) => sum + val, 0) : 0;  // Sum instead of average
    });
    
    categories = sortedYears;
  }

  // Calculate the goal line based on the daily goal multiplied by the number of days
  const goalTotal = goal * validData.length;
  
  // Get status based on total volumes vs goal
  const getStatus = () => {
    if (totalVolumes >= goalTotal) {
      return { 
        status: "great", 
        badge: "Exceeding Goal" 
      }
    }
    if (totalVolumes >= goalTotal * 0.8) {
      return { 
        status: "good", 
        badge: "Almost There" 
      }
    }
    return { 
      status: "critical", 
      badge: "Needs Attention" 
    }
  }

  // Calculate stats for the card
  const stats = {
    "Avg Daily Volume": avgVolumesPerDay,
    "Linked Appts": `${linkedApptsPercentage}%`,
    "Patients Per Chair": patientsPerChair
  };
  
  // Custom header component for BlankCard
  const customHeader = (
    <div className="flex flex-col">
      <div className="flex items-center gap-3 mb-1">
        <span className="text-3xl font-bold leading-tight">{totalVolumes}</span>
        {/* <div className="flex items-center gap-1">
          <div 
            className={`flex items-center gap-1 ${Number(trendPercentage) >= 0 ? 'text-green-500' : 'text-red-500'}`}
            title={`Trend: ${Number(trendPercentage) >= 0 ? '+' : ''}${trendPercentage}% compared to start of range`}
          >
            {Number(trendPercentage) >= 0 
              ? <FiTrendingUp className="w-4 h-4" /> 
              : <FiTrendingDown className="w-4 h-4" />
            }
            <span className="text-sm font-medium">
              {Number(trendPercentage) >= 0 ? '+' : ''}
              {trendPercentage}%
            </span>
          </div>
          <div 
            className="text-gray-400 hover:text-gray-600 cursor-help relative group"
          >
            <FiInfo className="w-3.5 h-3.5" />
            <div className="absolute z-50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-300 bottom-full left-1/2 transform -translate-x-1/2 -translate-y-2 px-4 py-4 bg-[#6955EA] text-white text-sm rounded-lg shadow-lg w-64 pointer-events-none">
              Trend is calculated as the percentage difference between the first and last point in the selected time range
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        </div> */}
      </div>
      <span className="text-md text-left text-gray-500">Total Completed Volumes</span>
    </div>
  );

  // Create a dropdown wrapper component to force re-render when grouping changes
  const GroupingDropdownWrapper = () => {
    return (
      <DropdownMenu
        buttonText={viewOptions.find(opt => opt.id === viewState)?.name}
        options={viewOptions}
        selectedOption={viewState}
        onSelect={setViewState}
        buttonClassName="inline-flex justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
      />
    );
  };

  // Custom footer with dropdown menu
  const customFooter = (
    <div className="mt-4 flex justify-left items-center pl-4 pb-2">
      <div className="w-auto">
        <label className="text-md text-gray-500 mb-1 inline-block">View by: &nbsp;</label>
        <GroupingDropdownWrapper />
      </div>
    </div>
  );
  
  // Return props for BlankCard
  const statusInfo = getStatus()
  return {
    title: "Completed Volumes", // This will be replaced by the custom header
    description: "",  // No description needed
    chartType: "line",
    chartData: {
      series: seriesData,
      categories: categories
    },
    minValue: Math.min(...seriesData),
    maxValue: Math.max(...seriesData),
    chartHeight,
    height,
    status: statusInfo.status,
    badge: statusInfo.badge,
    stats,
    customHeader, // Add custom header component
    customFooter, // Add custom footer component
    goal: goalTotal, // Total goal for all days
    isHigherBetter: true, // Higher values are better for this metric
    options: {
      yAxisFormatter: value => Math.round(value),
      statsGridClass: "grid-cols-3 gap-4",
      tooltipText: "Total Number of Patients",
      tooltipFormatter: value => `${value} patients`
    }
  };
};

export default createCompletedVolumes; 